import React, { ReactNode } from "react";
import { theme } from "src/config";
import { useClickOutside } from "src/util/util";
import styled from "styled-components";

const Drawer = styled.div<{hasBackdrop: boolean, size: string, isLeft: boolean, width: string}>`
  background-color: white;
  box-shadow:       ${props => props.hasBackdrop ? `${theme.boxShadow.medium}` : `${theme.boxShadow.large}`};
  height:           100vh;
  left:             ${props => props.isLeft ? props.size : undefined};
  overflow-y:       auto;
  overflow-x:       auto;
  position:         fixed;
  right:            ${props => !props.isLeft ? props.size : undefined};
  top:              0;
  transition:       0.4s ease-in-out;
  width:            ${props => props.width};
  z-index:          20;
  display:          flex;
  flex-direction:   column;
  justify-content:  space-between;
`;

const BodyContainer = styled.div`
  flex-grow:  1;
  width:      100%;
  position:   relative;
`;

const Backdrop = styled.div`
  width:            100%;
  height:           100%;
  background-color: ${theme.color.backdrop};
  position:         absolute;
  z-index:          10;
`;

// This should be animated with Framer motion
const PageBody = styled.div<{sidebarWidth: string}>`
  /* height:         100%; */
  display:        flex;
  flex-direction: column;
  width:          ${props => `calc(100vw - ${props.sidebarWidth})`};
  margin-left:    ${props => props.sidebarWidth};
  transition:     width 0.4s ease-in-out, margin-left 0.4s ease-in-out;
  overflow-x: auto;
  overflow-y: auto;
`;

const Header = styled.div`
  width:    100%;
`;

const Footer = styled.div`
  width:    100%;
`;

export interface ISidebarProps {
  anchor?:   "right" | "left",
  backdrop?: boolean
  children?: ReactNode,
  content:   ReactNode,
  onClose?:  (open: boolean) => void,
  open?:     boolean,
  overlay?:  boolean
  size?:     "small" | "medium" | "large" | string
  type?:     "static" | "dynamic" | "shrink",
  header?:   ReactNode,
  footer?:   ReactNode,
  closeOnClickOutside?: boolean;
}

export const SidebarContext = React.createContext({isSidebarOpen: false});

/**
 * The sidebar provides a context for the enclosed page as well as the sidebar content that holds the state whether the sidebar is open or not. 
 * Context can be used with the following hook:
 * 
 * const { isSidebarOpen } = useSidebarContext();
 * 
 * 
 * @param anchor   "right" | "left", defaults to "left"
 * @param backdrop boolean, Show a backdrop while sidebar is open that catches input. Clicking the backdrop will trigger onClose. Default is false
 * @param children ReactNode,
 * @param content  ReactNode, Content of the drawer
 * @param onClose  (open: boolean) => void, called when drawer is closed. Ignored for static sidebar
 * @param open     boolean, Defaults to false. Not used in static sidebar
 * @param overlay  boolean, Whether the page body should be pushed aside. Default is false
 * @param size     "small" | "medium" | "large" | string, Size of the sidebar, defaults to "medium". Any css size can be provided
 * @param type     "static" | "dynamic" | "shrink", A "static" sidebar is always open, a "dynamic" sidebar hides completely onClose and a "shrink" sidebar reduces size to a set value. Default is "dynamic"
 * @param header    Reactnode, Sticky header at the top of the sidebar
 * @param footer    Reactnode, Sticky footer at the bottom of the sidebar
 * @param closeOnClickOutside If true, clicking outside will close the sidebar. Ignored when backdrop is set (since it closes anyways)
 */
export const Sidebar = (props: ISidebarProps): JSX.Element => {
  const drawerRef = React.useRef<HTMLDivElement>();
  if(props.closeOnClickOutside) useClickOutside(drawerRef, () => props.open && props.onClose(false));

  const shrinkSize: string = "5.5rem";
  const side:       string = props.anchor || "left";
  const size = React.useMemo(() => {
    if(props.type === "shrink" && !props.open) return shrinkSize;

    if(!props.size) return "20%";

    switch(props.size) {
      case "small":  return "25rem";
      case "medium": return "20%";
      case "large":  return "30%";
      default: return props.size;
    }
  }, [props.size, props.type, props.open]);

  const [pagePos,    setPagePos]    = React.useState<string>((props.open || props.type === "static") ? size  : "0px");
  const [sidebarPos, setSidebarPos] = React.useState<string>((props.open || props.type === "static") ? "0px" : "-" + size);

  React.useEffect(() => {
    if(props.type === "shrink") {
      setPagePos(size);
      setSidebarPos("0px");
    }
    else {
      setPagePos((props.open || props.type === "static")    ? size  : "0px");
      setSidebarPos((props.open || props.type === "static") ? "0px" : "-" + size);
    }
  }, [props.open, props.type, props.size]);

  return (
    <SidebarContext.Provider value={{isSidebarOpen: props.open}}>
      <Drawer 
        size        = {sidebarPos}
        width       = {size}
        isLeft      = {side==="left"}
        hasBackdrop = {props.backdrop}
        ref         = {drawerRef}
      >
        {props.header && <Header>{props.header}</Header>}
        <BodyContainer>
          {props.content}
        </BodyContainer>
        {props.footer && <Footer>{props.footer}</Footer>}
      </Drawer>
      <PageBody sidebarWidth = {size}>
        {props.open && props.backdrop && <Backdrop onClick={() => props.onClose(false)}/>}
        {props.children}
      </PageBody>
    </SidebarContext.Provider>
  );
}